import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useCartStore, OfferWarningType, getAlertConfig } from '@restaurant/core'
import type { AlertProps } from '@restaurant/core'
import type { Offer } from '@restaurant/core'

interface OfferAlertCallbacks {
  onPrimary?: () => void
  onSecondary?: () => void
}

interface ShowWarningOptions {
  missingItemsCount?: number
  customMessage?: string
  minimumPurchase?: number
}

export interface OfferAlertHook {
  showWarning: (
    type: OfferWarningType,
    offer: Offer | null,
    callbacks: OfferAlertCallbacks,
    options?: ShowWarningOptions
  ) => void
  dismiss: () => void
  alertProps: Partial<AlertProps>
}

export interface UseOfferAlertsOptions {
  /** Called when the cart store auto-removes an offer. Return callbacks for the
   *  alert buttons so the user gets an interactive dialog instead of a plain dismiss.
   *  Return null to suppress the alert entirely (another instance handles it). */
  onAutoRemoval?: (warningType: OfferWarningType, offerId?: string) => OfferAlertCallbacks | null
}

export function useOfferAlerts(hookOptions?: UseOfferAlertsOptions): OfferAlertHook {
  const onAutoRemoval = hookOptions?.onAutoRemoval
  const [visible, setVisible] = useState(false)
  const [currentType, setCurrentType] = useState<OfferWarningType | null>(null)
  const [, setCurrentOffer] = useState<Offer | null>(null)
  const [callbacks, setCallbacks] = useState<OfferAlertCallbacks>({})
  const [options, setOptions] = useState<ShowWarningOptions>({})

  const lastOfferRemoval = useCartStore((s) => s.lastOfferRemoval)
  const clearLastOfferRemoval = useCartStore((s) => s.clearLastOfferRemoval)

  const dismiss = useCallback(() => {
    setVisible(false)
    setCurrentType(null)
    setCurrentOffer(null)
    setCallbacks({})
    setOptions({})
  }, [])

  // Track visibility in a ref so showWarning can read it without a dep
  const visibleRef = useRef(false)
  useEffect(() => {
    visibleRef.current = visible
  }, [visible])

  const showWarning = useCallback(
    (
      type: OfferWarningType,
      offer: Offer | null,
      cbs: OfferAlertCallbacks,
      opts?: ShowWarningOptions
    ) => {
      // Prevent double-fire: reject if an alert is already showing
      if (visibleRef.current) return

      setCurrentType(type)
      setCurrentOffer(offer)
      setCallbacks(cbs)
      setOptions(opts ?? {})
      setVisible(true)
    },
    []
  )

  // Watch cartStore.lastOfferRemoval — when non-null and not already showing,
  // show the appropriate alert then clear the removal record.
  // The `onAutoRemoval` callback, if provided, lets the consumer supply
  // actionable dialog buttons (e.g. "Back to Offers") instead of a plain dismiss.
  useEffect(() => {
    if (lastOfferRemoval && !visible) {
      const warningType = lastOfferRemoval.warningType as OfferWarningType
      const cbs = onAutoRemoval
        ? onAutoRemoval(warningType, lastOfferRemoval.offerId)
        : {}
      if (cbs === null) {
        // Consumer explicitly suppressed this alert type — another instance handles it
        clearLastOfferRemoval()
        return
      }
      showWarning(warningType, null, cbs, {
        minimumPurchase: lastOfferRemoval.minimumPurchase,
      })
      clearLastOfferRemoval()
    }
  }, [lastOfferRemoval, visible, showWarning, clearLastOfferRemoval, onAutoRemoval])

  const alertProps: Partial<AlertProps> = useMemo(() => {
    if (!visible || !currentType) {
      return { visible: false }
    }

    const config = getAlertConfig(currentType, {
      minimumPurchase: options.minimumPurchase,
      missingItemsCount: options.missingItemsCount,
      customMessage: options.customMessage,
    })

    return {
      visible,
      title: config.title,
      description: config.message,
      primaryAction: {
        text: config.primaryButtonText,
        onPress: () => {
          callbacks.onPrimary?.()
          dismiss()
        },
        testID: 'offer-warning-primary',
      },
      secondaryAction: config.secondaryButtonText
        ? {
            text: config.secondaryButtonText,
            variant: 'Secondary' as const,
            onPress: () => {
              callbacks.onSecondary?.()
              dismiss()
            },
            testID: 'offer-warning-secondary',
          }
        : undefined,
      onBackgroundPress: dismiss,
      onEscapeKey: dismiss,
      testID: 'offer-warning-alert',
    }
  }, [visible, currentType, options, callbacks, dismiss])

  return { showWarning, dismiss, alertProps }
}
